import React, {useEffect, useState, useCallback, useRef} from "react";
import {axiosInstance} from "../../../bricks/axios";
import {useModal} from "../../../context/ModalContext";
import Forms from "../../../bricks/form/Forms";
import {API, languageOptions} from "../../../config/Config";
import HelpUploadModalContent from "./HelpUploadModalContent";

import "./HelpAdministration.scss";

/**
 * Nový datový tvar:
 * {
 *   _id: "abc",
 *   label: "Nápověda / Brožura",
 *   filenameLocale: {
 *     "cs": { filename: "brozura-cs.pdf", locale: "cs" },
 *     "en": { filename: "brochure-en.pdf", locale: "en" },
 *     ...
 *   },
 *   createdAt, updatedAt ...
 * }
 */

const HelpAdministration = () => {
    const {openModal, confirm} = useModal();
    const [isLoading, setIsLoading] = useState(true);
    const [files, setFiles] = useState([]);
    const [error, setError] = useState(null);

    const langs = Object.keys(languageOptions);

    // ochrany proti duplicitám / StrictMode
    const fetchingRef = useRef(false);
    const didInitRef = useRef(false);

    const reload = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setIsLoading(true);
        try {
            const {method, url} = API.loadHelp;
            const {data} = await axiosInstance[method](url);
            setFiles(Array.isArray(data) ? data : []);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            fetchingRef.current = false;
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        let cancelled = false;
        (async () => {
            try {
                await reload();
            } catch (e) {
                if (!cancelled) setError(e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [reload]);

    // CREATE
    const handleCreate = () => {
        openModal(
            HelpUploadModalContent,
            {
                mode: "create",
                languageOptions,
                onSubmit: async (payload) => {
                    // payload = { label, filesByLang }
                    const fd = new FormData();
                    fd.append("label", payload.label ?? "");
                    for (const [loc, file] of Object.entries(payload.filesByLang || {})) {
                        if (file) fd.append(`files[${loc}]`, file);
                    }
                    for (const [lng, desc] of Object.entries(payload.descriptionsByLang || {})) {
                        fd.append(`filenameLocale[${lng}]`, desc ?? "");
                    }
                    const {method, url} = API.uploadHelpFile;
                    await axiosInstance[method](url, fd);
                    await reload();
                }
            },
            {title: "Nahrát soubory (podle jazyka)", size: "lg"}
        );
    };

    // EDIT
    const handleEdit = (record) => {
        openModal(
            HelpUploadModalContent,
            {
                mode: "edit",
                languageOptions,
                initial: {
                    _id: record._id,
                    label: record.label || "",
                    filenameLocale: record.filenameLocale || {}
                },
                onSubmit: async (payload) => {
                    // payload = { label, filesByLang (jen změněné jazyky) }
                    const fd = new FormData();
                    fd.append("id", record._id);
                    fd.append("label", payload.label ?? "");
                    for (const [loc, file] of Object.entries(payload.filesByLang || {})) {
                        if (file) fd.append(`files[${loc}]`, file);
                    }
                    for (const [lng, desc] of Object.entries(payload.descriptionsByLang || {})) {
                        if (payload.descriptionsByLang?.[lng]) {
                            fd.append(`filenameLocale[${lng}]`, desc ?? "");
                        }
                    }
                    const {method, url} = API.updateHelpFile(record._id);
                    await axiosInstance[method](url, fd);
                    await reload();
                }
            },
            {title: `Upravit: ${record.label || record._id}`, size: "lg"}
        );
    };

    const handleDelete = async (record) => {
        const result = await confirm(`Opravdu chete smazat "${record.label || record._id}"?`);
        if (!result) return;
        try {
            const {method, url} = API.deleteHelpFile(record._id);
            await axiosInstance[method](url);
            await reload();
        } catch (e) {
            setError(e);
        }
    };

    const renderTable = () => {
        if (isLoading) return <div>Načítám…</div>;
        if (error) return <div className="error">Chyba: {String(error?.message || error)}</div>;
        if (!files.length) return <div>Žádné soubory zatím nejsou nahrané.</div>;

        return (
            <div className="file-table-wrapper">
                <table className="file-table">
                    <thead>
                    <tr>
                        <th>Popis</th>
                        {langs.map((lng) => (
                            <th key={lng} title={languageOptions[lng]}>
                                {lng.toUpperCase()}
                            </th>
                        ))}
                        <th>Akce</th>
                    </tr>
                    </thead>
                    <tbody>
                    {files.map((f) => {
                        const fileLoc = f.filenameLocale || {};
                        return (
                            <tr key={f._id}>
                                <td className="filename">
                                    <div className="name">{f.label || "—"}</div>
                                </td>

                                {langs.map((lng) => {
                                    const cell = fileLoc?.[lng];
                                    const fname = cell?.filename.substr(17, 20) || null;
                                    return (
                                        <td key={`${f._id}_${lng}`} className="variant">
                                            {fname ? <>✓ {fname}</> : <span className="muted">—</span>}
                                        </td>
                                    );
                                })}

                                <td className="actions">
                                    <Forms.Button label="Upravit" onClick={() => handleEdit(f)}/>
                                    &nbsp;
                                    <Forms.Button variant="danger" label="Smazat" onClick={() => handleDelete(f)}/>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="HelpAdministration">
            <div className="header">
                <h2>Soubory nápovedy</h2>
                <div className="controls">
                    <Forms.Button label="Nahrát soubor" onClick={handleCreate}/>
                </div>
            </div>

            {renderTable()}
        </div>
    );
};

export default HelpAdministration;
